/**
 * Dialog para criar novo Status do WhatsApp
 * 
 * Suporta criação de status de texto, imagem e vídeo
 * com opção de agendamento para publicação futura
 * e sistema de lote para publicar múltiplos status em sequência
 * 
 * @version 1.1.0
 * @release 2025-02-08
 */

import { useState, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { Input } from '@/components/ui/input';
import { MediaUrlInput } from '@/components/ui/media-url-input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { cn } from '@/lib/utils';
import { 
  Type, 
  Image, 
  Video, 
  CalendarIcon, 
  Clock, 
  Loader2,
  Send,
  Plus,
  Trash2,
  Layers,
} from 'lucide-react';
import type { StatusType, CreateStatusParams } from '@/hooks/useWhatsAppStatus';
import type { InboxData } from '@/hooks/useInboxes';

// ============================================================================
// TIPOS E INTERFACES
// ============================================================================

/** Item individual de status no lote */
interface StatusBatchItem {
  id: string;
  status_type: StatusType;
  content?: string;
  media_url?: string;
  media_caption?: string;
}

// Schema de validação - com campos separados para imagem e vídeo
const createStatusSchema = z.object({
  content: z.string().optional(),
  image_url: z.string().url('URL inválida').optional().or(z.literal('')),
  video_url: z.string().url('URL inválida').optional().or(z.literal('')),
  media_caption: z.string().max(500, 'Legenda muito longa').optional(),
  scheduled_date: z.date().optional(),
  scheduled_time: z.string().optional(),
});

type CreateStatusFormData = z.infer<typeof createStatusSchema>;

interface CreateStatusDialogProps {
  /** Se o dialog está aberto */
  open: boolean;
  /** Callback ao fechar */
  onOpenChange: (open: boolean) => void;
  /** Inbox selecionada para criar o status */
  inbox: InboxData | null;
  /** Callback ao criar o status */
  onCreateStatus: (params: CreateStatusParams) => Promise<void>;
  /** Se está criando */
  isCreating?: boolean;
}

// ============================================================================
// COMPONENTE PRINCIPAL
// ============================================================================

/**
 * Dialog para criação de novo status do WhatsApp
 * Suporta publicação individual ou em lote (sequência)
 */
export function CreateStatusDialog({
  open,
  onOpenChange,
  inbox,
  onCreateStatus,
  isCreating,
}: CreateStatusDialogProps) {
  // Estado do tipo de status selecionado
  const [statusType, setStatusType] = useState<StatusType>('text');
  
  // Estado do agendamento
  const [isScheduled, setIsScheduled] = useState(false);

  // Estado do modo de lote
  const [isBatchMode, setIsBatchMode] = useState(false);
  const [batchItems, setBatchItems] = useState<StatusBatchItem[]>([]);
  const [isPublishingBatch, setIsPublishingBatch] = useState(false);

  // Form com react-hook-form + zod
  const form = useForm<CreateStatusFormData>({
    resolver: zodResolver(createStatusSchema),
    defaultValues: {
      content: '',
      image_url: '',
      video_url: '',
      media_caption: '',
      scheduled_time: '12:00',
    },
  });

  // Reset form ao abrir/fechar
  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen) {
      form.reset();
      setStatusType('text');
      setIsScheduled(false);
      setIsBatchMode(false);
      setBatchItems([]);
    }
    onOpenChange(isOpen);
  };

  // Obter URL de mídia baseado no tipo atual
  const getMediaUrl = useCallback((data: CreateStatusFormData, type: StatusType): string | undefined => {
    if (type === 'image') return data.image_url || undefined;
    if (type === 'video') return data.video_url || undefined;
    return undefined;
  }, []);

  // Adicionar item ao lote
  const handleAddToBatch = useCallback(() => {
    const data = form.getValues();
    
    // Validação básica
    const content = statusType === 'text' ? data.content?.trim() : undefined;
    const mediaUrl = getMediaUrl(data, statusType);
    
    if (statusType === 'text' && !content) {
      form.setError('content', { message: 'Digite o texto do status' });
      return;
    }
    
    if (statusType !== 'text' && !mediaUrl) {
      const fieldName = statusType === 'image' ? 'image_url' : 'video_url';
      form.setError(fieldName, { message: 'Informe a URL da mídia' });
      return;
    }

    // Adiciona ao lote
    const newItem: StatusBatchItem = {
      id: crypto.randomUUID(),
      status_type: statusType,
      content,
      media_url: mediaUrl,
      media_caption: statusType !== 'text' ? data.media_caption : undefined,
    };

    setBatchItems(prev => [...prev, newItem]);

    // Limpa campos após adicionar
    form.setValue('content', '');
    form.setValue('image_url', '');
    form.setValue('video_url', '');
    form.setValue('media_caption', '');
  }, [form, statusType, getMediaUrl]);

  // Remover item do lote
  const handleRemoveFromBatch = useCallback((itemId: string) => {
    setBatchItems(prev => prev.filter(item => item.id !== itemId));
  }, []);

  // Publicar lote em sequência
  const handlePublishBatch = useCallback(async () => {
    if (!inbox || batchItems.length === 0) return;

    setIsPublishingBatch(true);

    try {
      // Monta data de agendamento se ativado (para o primeiro item)
      let scheduledAt: string | undefined;
      const data = form.getValues();
      
      if (isScheduled && data.scheduled_date && data.scheduled_time) {
        const [hours, minutes] = data.scheduled_time.split(':').map(Number);
        const scheduledDate = new Date(data.scheduled_date);
        scheduledDate.setHours(hours, minutes, 0, 0);
        scheduledAt = scheduledDate.toISOString();
      }

      // Publica cada item em sequência
      for (let i = 0; i < batchItems.length; i++) {
        const item = batchItems[i];
        
        await onCreateStatus({
          inbox_id: inbox.id,
          status_type: item.status_type,
          content: item.content,
          media_url: item.media_url,
          media_caption: item.media_caption,
          // Agenda apenas o primeiro item; os demais serão publicados em sequência
          scheduled_at: i === 0 ? scheduledAt : undefined,
        });

        // Pequeno delay entre publicações para evitar rate limit
        if (i < batchItems.length - 1) {
          await new Promise(resolve => setTimeout(resolve, 2000));
        }
      }

      // Fecha o dialog após sucesso
      handleOpenChange(false);
    } finally {
      setIsPublishingBatch(false);
    }
  }, [inbox, batchItems, form, isScheduled, onCreateStatus, handleOpenChange]);

  // Submit do formulário (publicação individual)
  const onSubmit = async (data: CreateStatusFormData) => {
    if (!inbox) return;

    // Monta data de agendamento se ativado
    let scheduledAt: string | undefined;
    if (isScheduled && data.scheduled_date && data.scheduled_time) {
      const [hours, minutes] = data.scheduled_time.split(':').map(Number);
      const scheduledDate = new Date(data.scheduled_date);
      scheduledDate.setHours(hours, minutes, 0, 0);
      scheduledAt = scheduledDate.toISOString();
    }

    // Obtém URL correta baseado no tipo selecionado
    const mediaUrl = getMediaUrl(data, statusType);

    // Cria o status
    await onCreateStatus({
      inbox_id: inbox.id,
      status_type: statusType,
      content: statusType === 'text' ? data.content : undefined,
      media_url: mediaUrl,
      media_caption: statusType !== 'text' ? data.media_caption : undefined,
      scheduled_at: scheduledAt,
    });

    // Fecha o dialog
    handleOpenChange(false);
  };

  // Função auxiliar para renderizar ícone do tipo de status
  const getStatusTypeIcon = (type: StatusType) => {
    switch (type) {
      case 'text': return <Type className="h-3 w-3" />;
      case 'image': return <Image className="h-3 w-3" />;
      case 'video': return <Video className="h-3 w-3" />;
    }
  };

  // Função auxiliar para renderizar preview do item do lote
  const getItemPreview = (item: StatusBatchItem): string => {
    if (item.content) {
      return item.content.length > 50 ? `${item.content.slice(0, 50)}...` : item.content;
    }
    if (item.media_url) {
      // Extrai nome do arquivo da URL
      const urlParts = item.media_url.split('/');
      const fileName = urlParts[urlParts.length - 1].split('?')[0];
      return fileName.length > 30 ? `${fileName.slice(0, 30)}...` : fileName;
    }
    return 'Item vazio';
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      {/* DialogContent com layout robusto: flex-col, altura fixa, scroll interno */}
      <DialogContent className="!flex !flex-col !p-0 !gap-0 w-[calc(100vw-2rem)] sm:w-full sm:max-w-[560px] max-h-[85vh] h-[85vh] overflow-hidden">
        {/* Header fixo */}
        <DialogHeader className="flex-shrink-0 border-b px-6 py-4">
          <DialogTitle className="flex items-center gap-2">
            Criar Status
            {isBatchMode && batchItems.length > 0 && (
              <Badge variant="secondary" className="ml-2">
                <Layers className="h-3 w-3 mr-1" />
                {batchItems.length} no lote
              </Badge>
            )}
          </DialogTitle>
          <DialogDescription>
            {inbox ? (
              <>Publicar status na inbox <strong>{inbox.name}</strong></>
            ) : (
              'Selecione uma inbox para criar o status'
            )}
          </DialogDescription>
        </DialogHeader>

        {/* Corpo scrollável */}
        <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden px-6 py-4">
          <Form {...form}>
            <form id="create-status-form" onSubmit={form.handleSubmit(onSubmit)} className="space-y-4 min-w-0">
              {/* Toggle modo lote */}
              <div className="flex items-center justify-between rounded-lg border p-3 bg-muted/30">
                <div className="space-y-0.5">
                  <Label htmlFor="batch-toggle" className="text-sm font-medium flex items-center gap-2">
                    <Layers className="h-4 w-4" />
                    Modo Lote (Sequência)
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    Adicione vários status para publicar em sequência
                  </p>
                </div>
                <Switch
                  id="batch-toggle"
                  checked={isBatchMode}
                  onCheckedChange={setIsBatchMode}
                />
              </div>

              {/* Lista de itens do lote */}
              {isBatchMode && batchItems.length > 0 && (
                <div className="rounded-lg border p-3 space-y-2">
                  <Label className="text-sm font-medium">Itens no Lote</Label>
                  <ScrollArea className="h-[120px]">
                    <div className="space-y-2 pr-3">
                      {batchItems.map((item, index) => (
                        <div
                          key={item.id}
                          className="flex items-center gap-2 p-2 rounded-md bg-muted/50 text-sm"
                        >
                          <span className="flex-shrink-0 w-5 h-5 rounded-full bg-primary/10 text-primary text-xs flex items-center justify-center font-medium">
                            {index + 1}
                          </span>
                          <span className="flex-shrink-0">
                            {getStatusTypeIcon(item.status_type)}
                          </span>
                          <span className="flex-1 truncate min-w-0 text-muted-foreground">
                            {getItemPreview(item)}
                          </span>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6 flex-shrink-0 text-destructive hover:text-destructive"
                            onClick={() => handleRemoveFromBatch(item.id)}
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                </div>
              )}

              {/* Tabs de tipo de status */}
              <Tabs
                value={statusType}
                onValueChange={(v) => setStatusType(v as StatusType)}
              >
                {/* TabsList com !grid para override do inline-flex base */}
                <TabsList className="!grid w-full grid-cols-3">
                  <TabsTrigger value="text" className="gap-2 min-w-0">
                    <Type className="h-4 w-4" />
                    Texto
                  </TabsTrigger>
                  <TabsTrigger value="image" className="gap-2 min-w-0">
                    <Image className="h-4 w-4" />
                    Imagem
                  </TabsTrigger>
                  <TabsTrigger value="video" className="gap-2 min-w-0">
                    <Video className="h-4 w-4" />
                    Vídeo
                  </TabsTrigger>
              </TabsList>

              {/* Conteúdo: Texto */}
              <TabsContent value="text" className="space-y-4 mt-4">
                <FormField
                  control={form.control}
                  name="content"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Texto do Status</FormLabel>
                      <FormControl>
                        <Textarea
                          placeholder="Digite o texto do seu status..."
                          className="min-h-[120px] resize-none"
                          {...field}
                        />
                      </FormControl>
                      <FormDescription>
                        O texto será exibido como status por 24 horas
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </TabsContent>

              {/* Conteúdo: Imagem - usa campo image_url separado */}
              <TabsContent value="image" className="space-y-4 mt-4">
                <FormField
                  control={form.control}
                  name="image_url"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>URL da Imagem</FormLabel>
                      <FormControl>
                        <MediaUrlInput
                          value={field.value || null}
                          onChange={(url) => field.onChange(url || '')}
                          placeholder="https://exemplo.com/imagem.jpg"
                          showPreview={true}
                          allowedTypes={['image']}
                        />
                      </FormControl>
                      <FormDescription>
                        Cole uma URL ou selecione da galeria
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="media_caption"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Legenda (opcional)</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="Adicione uma legenda..."
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </TabsContent>

              {/* Conteúdo: Vídeo - usa campo video_url separado */}
              <TabsContent value="video" className="space-y-4 mt-4">
                <FormField
                  control={form.control}
                  name="video_url"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>URL do Vídeo</FormLabel>
                      <FormControl>
                        <MediaUrlInput
                          value={field.value || null}
                          onChange={(url) => field.onChange(url || '')}
                          placeholder="https://exemplo.com/video.mp4"
                          showPreview={false}
                          allowedTypes={['video']}
                        />
                      </FormControl>
                      <FormDescription>
                        Cole uma URL ou selecione da galeria (máx 10MB)
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="media_caption"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Legenda (opcional)</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="Adicione uma legenda..."
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </TabsContent>
            </Tabs>

            {/* Botão adicionar ao lote (apenas no modo lote) */}
            {isBatchMode && (
              <Button
                type="button"
                variant="outline"
                className="w-full"
                onClick={handleAddToBatch}
              >
                <Plus className="h-4 w-4 mr-2" />
                Adicionar ao Lote
              </Button>
            )}

            {/* Toggle de Agendamento */}
            <div className="flex items-center justify-between rounded-lg border p-3">
              <div className="space-y-0.5">
                <Label htmlFor="schedule-toggle" className="text-sm font-medium">
                  Agendar publicação
                </Label>
                <p className="text-xs text-muted-foreground">
                  {isBatchMode 
                    ? 'O primeiro item será agendado, os demais seguirão em sequência'
                    : 'Escolha uma data e hora para publicar'
                  }
                </p>
              </div>
              <Switch
                id="schedule-toggle"
                checked={isScheduled}
                onCheckedChange={setIsScheduled}
              />
            </div>

              {/* Campos de Agendamento - responsivo */}
              {isScheduled && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="scheduled_date"
                  render={({ field }) => (
                    <FormItem className="flex flex-col">
                      <FormLabel>Data</FormLabel>
                      <Popover>
                        <PopoverTrigger asChild>
                          <FormControl>
                            <Button
                              variant="outline"
                              className={cn(
                                'pl-3 text-left font-normal',
                                !field.value && 'text-muted-foreground'
                              )}
                            >
                              {field.value ? (
                                format(field.value, 'dd/MM/yyyy', { locale: ptBR })
                              ) : (
                                <span>Selecione</span>
                              )}
                              <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                            </Button>
                          </FormControl>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                          <Calendar
                            mode="single"
                            selected={field.value}
                            onSelect={field.onChange}
                            disabled={(date) => date < new Date()}
                            locale={ptBR}
                            initialFocus
                          />
                        </PopoverContent>
                      </Popover>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="scheduled_time"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Hora</FormLabel>
                      <FormControl>
                        <div className="relative">
                          <Clock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                          <Input
                            type="time"
                            className="pl-9"
                            {...field}
                          />
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            )}

            </form>
          </Form>
        </div>

        {/* Footer fixo com botões */}
        <div className="flex-shrink-0 border-t bg-background px-6 py-4 flex justify-end gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => handleOpenChange(false)}
            disabled={isCreating || isPublishingBatch}
          >
            Cancelar
          </Button>

          {/* Botão condicional: lote ou individual */}
          {isBatchMode ? (
            <Button 
              type="button" 
              disabled={isPublishingBatch || batchItems.length === 0 || !inbox}
              onClick={handlePublishBatch}
            >
              {isPublishingBatch ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Publicando {batchItems.length} status...
                </>
              ) : (
                <>
                  <Layers className="mr-2 h-4 w-4" />
                  {isScheduled ? 'Agendar Lote' : 'Publicar Lote'} ({batchItems.length})
                </>
              )}
            </Button>
          ) : (
            <Button type="submit" form="create-status-form" disabled={isCreating || !inbox}>
              {isCreating ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {isScheduled ? 'Agendando...' : 'Publicando...'}
                </>
              ) : (
                <>
                  <Send className="mr-2 h-4 w-4" />
                  {isScheduled ? 'Agendar' : 'Publicar'}
                </>
              )}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
